import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import projectService, { ProjectListItem } from '../../services/api/projectService';

interface ProjectState {
  projects: ProjectListItem[];
  activeProjectId: string | null;
  loading: boolean;
  error: string | null;
}

const initialState: ProjectState = {
  projects: [],
  activeProjectId: localStorage.getItem('activeProjectId'),
  loading: false,
  error: null,
};

export const fetchProjects = createAsyncThunk(
  'projects/fetchProjects',
  async (_, { rejectWithValue }) => {
    try {
      return await projectService.getProjects();
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to load projects');
    }
  }
);

const persistActiveProject = (projectId: string | null) => {
  if (projectId) {
    localStorage.setItem('activeProjectId', projectId);
  } else {
    localStorage.removeItem('activeProjectId');
  }
};

const projectSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    setActiveProject: (state, action: PayloadAction<string | null>) => {
      state.activeProjectId = action.payload;
      persistActiveProject(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchProjects.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchProjects.fulfilled, (state, action) => {
      state.loading = false;
      state.projects = action.payload;

      const activeProjectIsVisible = action.payload.some(
        (project) => project.id === state.activeProjectId
      );

      if (!activeProjectIsVisible) {
        const nextActiveProjectId = action.payload[0]?.id || null;
        state.activeProjectId = nextActiveProjectId;
        persistActiveProject(nextActiveProjectId);
      }
    });
    builder.addCase(fetchProjects.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { setActiveProject } = projectSlice.actions;
export default projectSlice.reducer;
